import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, MapPin, Clock, Search, Map as MapIcon, Image as ImageIcon, SlidersHorizontal, X } from 'lucide-react';
import { MapEmbed } from '../components/MapEmbed';
import { WishlistButton } from '../components/WishlistButton';
import { useWishlist } from '../hooks/useWishlist';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';
import { useLang } from '../context/LanguageContext';

const BUDGETS = [
  { id: 'all', label: 'Any budget', test: () => true },
  { id: 'under15k', label: 'Under ₹15k', test: (p) => p < 15000 },
  { id: '15to25k', label: '₹15k – ₹25k', test: (p) => p >= 15000 && p <= 25000 },
  { id: '25to45k', label: '₹25k – ₹45k', test: (p) => p > 25000 && p <= 45000 },
  { id: 'above45k', label: 'Above ₹45k', test: (p) => p > 45000 },
];

const DURATIONS = [
  { id: 'all', label: 'Any duration', test: () => true },
  { id: 'short', label: 'Up to 4 days', test: (d) => d <= 4 },
  { id: 'medium', label: '5 – 6 days', test: (d) => d >= 5 && d <= 6 },
  { id: 'long', label: '7+ days', test: (d) => d >= 7 },
];

const daysOf = (duration) => parseInt(duration, 10) || 0;

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-4 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
      active ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300'
    }`}
  >
    {children}
  </button>
);

const DestinationCard = ({ destination, onBook, wished, onWish }) => {
  const [showMap, setShowMap] = useState(false);
  const { t, loc } = useLang();
  const slug = destination.name.toLowerCase();

  return (
    <Card
      data-testid={`destination-card-${slug}`}
      className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white"
    >
      <div className="relative h-64 overflow-hidden">
        {showMap ? (
          <MapEmbed query={destination.map_query} className="w-full h-64" testId={`destination-map-${slug}`} />
        ) : (
          <img
            src={destination.image}
            alt={destination.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        )}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <div className="bg-white px-3 py-1 rounded-full flex items-center space-x-1 shadow h-10">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-semibold">{destination.rating}</span>
          </div>
          <WishlistButton active={wished} onClick={onWish} testId={`wishlist-destination-${slug}`} />
        </div>
        <div className="absolute top-4 left-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold capitalize">
          {destination.category}
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{loc(destination, 'name')}</h3>
        <div className="flex items-center text-gray-600 mb-3">
          <MapPin className="w-4 h-4 mr-1" />
          <span className="text-sm">{loc(destination, 'location')}</span>
        </div>
        <p className="text-gray-600 mb-4">{loc(destination, 'description')}</p>
        <div className="flex items-center justify-between mb-4">
          <Price inr={destination.price} className="text-xl text-teal-600 font-semibold" />
          <div className="flex items-center text-gray-500 text-sm">
            <Clock className="w-4 h-4 mr-1" />
            <span>{destination.duration}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid={`toggle-map-${slug}`}
            variant="outline"
            onClick={() => setShowMap((v) => !v)}
            className="flex-1 border-2 border-teal-500 text-teal-600 hover:bg-teal-50"
          >
            {showMap ? <ImageIcon className="w-4 h-4 mr-2" /> : <MapIcon className="w-4 h-4 mr-2" />}
            {showMap ? t('photo') : t('viewMap')}
          </Button>
          <Button
            data-testid={`book-now-${slug}`}
            onClick={() => onBook(destination)}
            className="flex-1 bg-sun-500 hover:bg-sun-600 text-white"
          >
            {t('bookNow')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Destinations = ({ user }) => {
  const navigate = useNavigate();
  const { loc } = useLang();
  const wishlist = useWishlist(user);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [budget, setBudget] = useState('all');
  const [duration, setDuration] = useState('all');

  useEffect(() => {
    api.get('/destinations')
      .then((res) => setDestinations(res.data))
      .catch((err) => console.error('Failed to load destinations', err))
      .finally(() => setLoading(false));
  }, []);

  const term = searchTerm.toLowerCase();
  const budgetRule = BUDGETS.find((b) => b.id === budget);
  const durationRule = DURATIONS.find((d) => d.id === duration);
  const filteredDestinations = destinations.filter((d) => {
    const matchesSearch = d.name.toLowerCase().includes(term) || d.location.toLowerCase().includes(term) || loc(d, 'name').includes(searchTerm) || loc(d, 'location').includes(searchTerm);
    const matchesCategory = activeCategory === 'all' || d.category === activeCategory;
    return matchesSearch && matchesCategory && budgetRule.test(d.price) && durationRule.test(daysOf(d.duration));
  });
  const filtersActive = budget !== 'all' || duration !== 'all';

  const handleBookNow = (destination) => navigate('/booking', { state: { destination } });

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="destinations-page">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Explore Destinations</h1>
          <p className="text-xl text-teal-50">Discover amazing places across India and the world</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="destination-search-input"
              type="text"
              placeholder="Search destinations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg border-2 border-gray-200 focus:border-teal-500 rounded-xl"
            />
          </div>
        </div>

        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 h-12">
            <TabsTrigger data-testid="category-tab-all" value="all" className="text-lg">All</TabsTrigger>
            <TabsTrigger data-testid="category-tab-indian" value="indian" className="text-lg">Indian</TabsTrigger>
            <TabsTrigger data-testid="category-tab-international" value="international" className="text-lg">International</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-10 space-y-4" data-testid="destination-filters">
          <div className="flex items-center justify-between">
            <p className="flex items-center text-sm font-semibold text-gray-700 uppercase tracking-wide">
              <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
            </p>
            {filtersActive && (
              <button data-testid="clear-filters-btn" onClick={() => { setBudget('all'); setDuration('all'); }} className="text-sm text-teal-600 hover:underline inline-flex items-center">
                <X className="w-4 h-4 mr-1" /> Clear
              </button>
            )}
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500 mr-1">Budget:</span>
              {BUDGETS.map((b) => (
                <Chip key={b.id} active={budget === b.id} onClick={() => setBudget(b.id)} testId={`budget-${b.id}`}>{b.label}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500 mr-1">Duration:</span>
              {DURATIONS.map((d) => (
                <Chip key={d.id} active={duration === d.id} onClick={() => setDuration(d.id)} testId={`duration-${d.id}`}>{d.label}</Chip>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <p data-testid="destinations-loading" className="text-center text-gray-500 py-12">Loading destinations...</p>
        ) : filteredDestinations.length === 0 ? (
          <div className="text-center py-12">
            <p data-testid="destinations-empty" className="text-gray-500 text-lg">No destinations match these filters. Try widening your budget or duration.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4" data-testid="destinations-count">{filteredDestinations.length} destination{filteredDestinations.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="destinations-grid">
              {filteredDestinations.map((destination) => (
                <DestinationCard
                  key={destination.id}
                  destination={destination}
                  onBook={handleBookNow}
                  wished={wishlist.has('destination', destination.id)}
                  onWish={() => wishlist.toggle('destination', destination.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Destinations;
